import type { Customer } from './types'
import { scheduleDate, scheduleInstant } from './scheduling'

export interface JourneyScheduleStep {
  clientStepId: string
  serviceId: number
  sequence: number
  sortOrder: number
  serviceConfigId?: number
  resourceId?: number
}
export interface JourneyScheduleOption {
  serviceConfigId: number
  sessionId: number
  resourceId?: number
  resourceName?: string
  resources?: { id: number; name: string }[]
  start: string
  end: string
  bookingMode: 'appointment' | 'estimate' | 'session'
}
export type JourneyScheduleChoices = Record<string, JourneyScheduleOption>
export interface JourneyScheduleDay {
  date: string
  hasCompleteJourney: boolean
  availableStepIds?: string[]
}
export interface JourneyScheduleRequest {
  templateId: number | null
  steps: JourneyScheduleStep[]
}
export interface JourneyScheduleBooking extends JourneyScheduleRequest {
  scheduledDate: string
  customer: Customer
  priority: number
  metadata: Record<string, string>
  choices: JourneyScheduleChoices
  stepFields: Record<string, Record<string, string>>
  idempotencyKey: string
}

export function normalizeScheduleSteps(steps: JourneyScheduleStep[]): JourneyScheduleStep[] {
  const levels = [...new Set(steps.map((step) => step.sequence))].sort((a, b) => a - b)
  return [...steps].sort((a, b) => a.sequence - b.sequence || a.sortOrder - b.sortOrder)
    .map((step, index) => ({ ...step, sequence: levels.indexOf(step.sequence) + 1, sortOrder: index + 1 }))
}

// Search the full day, including same-level services, rather than intersecting isolated vacancies.
export function findJourneySchedule(steps: JourneyScheduleStep[], options: Record<string, JourneyScheduleOption[]>, date: string, fixed: JourneyScheduleChoices = {}): JourneyScheduleChoices | null {
  if (!steps.length) {return null}
  const ordered = [...steps].sort((a, b) => a.sequence - b.sequence || a.sortOrder - b.sortOrder)
  const search = (index: number, choices: JourneyScheduleChoices): JourneyScheduleChoices | null => {
    if (index === ordered.length) {return choices}
    const step = ordered[index]
    for (const option of options[step.clientStepId] ?? []) {
      const start = scheduleInstant(option.start)
      const end = scheduleInstant(option.end)
      const pinned = fixed[step.clientStepId]
      if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start || scheduleDate(new Date(start)) !== date || (option.bookingMode !== 'session' && scheduleDate(new Date(end)) !== date)) {continue}
      if (step.serviceConfigId && step.serviceConfigId !== option.serviceConfigId) {continue}
      if (step.resourceId && !scheduleOptionSupportsResource(option, step.resourceId)) {continue}
      if (pinned && !sameScheduleOption(pinned, option)) {continue}
      if (!ordered.slice(0, index).every((previous) => {
        const selected = choices[previous.clientStepId]
        if (option.bookingMode === 'session' || selected.bookingMode === 'session') {return true}
        const otherStart = scheduleInstant(selected.start)
        const otherEnd = scheduleInstant(selected.end)
        return previous.sequence < step.sequence ? start >= otherEnd : end <= otherStart || start >= otherEnd
      })) {continue}
      const resource = option.resources?.find((item) => item.id === step.resourceId)
      const selected = pinned ?? (resource ? { ...option, resourceId: resource.id, resourceName: resource.name } : option)
      const complete = search(index + 1, { ...choices, [step.clientStepId]: selected })
      if (complete) {return complete}
    }
    return null
  }
  return search(0, {})
}
export const scheduleOptionSupportsResource = (option: JourneyScheduleOption, resourceId: number): boolean =>
  option.resourceId === resourceId || (option.bookingMode === 'session' && !option.resourceId && !!option.resources?.some((resource) => resource.id === resourceId))
export const sameScheduleOption = (a: JourneyScheduleOption, b: JourneyScheduleOption) =>
  a.serviceConfigId === b.serviceConfigId && a.sessionId === b.sessionId && a.start === b.start && a.end === b.end &&
  (a.resourceId === b.resourceId || (!!a.resourceId && scheduleOptionSupportsResource(b, a.resourceId)) || (!!b.resourceId && scheduleOptionSupportsResource(a, b.resourceId)))
